import { Injectable, signal, WritableSignal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import {
  PositionData,
  OpenPositionsResponse,
} from '@interfaces/open-positions.model';
import { GlobalErrorService } from '@services/global-error.service';
import { environment } from '@environments/environment';
import { DataService } from '@services/data.service';

@Injectable({
  providedIn: 'root',
})
export class HiringManagerOpenpositionsService {
  private baseUrl = environment.apiUrl;
  jobDetails: PositionData[] = [];
  openPositionsSignal: WritableSignal<any> = signal(null);
  private dataLoading = false;
  private reloadSubject = new BehaviorSubject<boolean>(false);
  reload$ = this.reloadSubject.asObservable();

  constructor(
    private http: HttpClient,
    private errorHandler: GlobalErrorService,
    private dataService: DataService,
  ) {}

  getOpenPositions(): Observable<OpenPositionsResponse> {
    if (!this.openPositionsSignal() && !this.dataLoading) {
      this.fetchOpenPositions();
    }
    return of(this.openPositionsSignal() || []);
  }

  refreshOpenPositions() {
    this.fetchOpenPositions();
  }

  private fetchOpenPositions() {
    // A fetch is already in flight. The component polls every second and may
    // call refresh again while we wait for the profile — don't stack duplicate
    // requests.
    if (this.dataLoading) {
      return;
    }
    // userProfile hydrates asynchronously (the signal starts as null). Firing
    // with a blank empId/email makes the backend match nobody and return an
    // empty list, which then gets cached and rendered as "zero positions" (the
    // intermittent bug users hit on fresh load / hard refresh). Skip the call
    // until the profile is available; the component's poll retries until then.
    const userProfile = this.dataService.userProfile();
    const empId = userProfile?.employeeId;
    const employeeLevel = userProfile?.employeeLevel;
    const email = userProfile?.email;
    if (!empId || !email) {
      return;
    }

    this.dataLoading = true;
    let params = new HttpParams()
      .set('empId', empId)
      .set('employeeLevel', employeeLevel)
      .set('email', email);

    this.http
      .get<
        OpenPositionsResponse[]
      >(`${this.baseUrl}/hiring/open-positions`, { params })
      .subscribe({
        next: (data) => {
          this.openPositionsSignal.set(data);
          this.dataLoading = false;
        },
        error: (error) => {
          this.errorHandler.logError(error);
          this.dataLoading = false;
        },
      });
  }

  forceReload() {
    this.reloadSubject.next(true);
  }
}
